import { useCallback, useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import SubmissionsTable from "@/components/SubmissionsTable";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { CurrencyCircleDollar, Truck, UsersThree, Receipt } from "@phosphor-icons/react";

export default function CeoPanel({ staff }) {
  const [subs, setSubs] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const refresh = useCallback(() => {
    api.get("/submissions").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/analytics/overview").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const cards = analytics ? [
    ["Total Revenue", `RM ${analytics.total_revenue.toLocaleString("en-MY", { minimumFractionDigits: 2 })}`, CurrencyCircleDollar, "text-primary"],
    ["Total Jobs", analytics.total_jobs, Truck, "text-foreground"],
    ["Runner Commissions", `RM ${analytics.runner_commissions.toLocaleString("en-MY", { minimumFractionDigits: 2 })}`, UsersThree, "text-sky-400"],
    ["Driver Commissions", `RM ${analytics.driver_commissions.toLocaleString("en-MY", { minimumFractionDigits: 2 })}`, Receipt, "text-emerald-400"],
  ] : [];

  return (
    <Tabs defaultValue="analytics" data-testid="ceo-panel">
      <TabsList className="mb-6 rounded-sm border border-border bg-card">
        <TabsTrigger data-testid="tab-ceo-analytics" value="analytics" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Financial Analytics</TabsTrigger>
        <TabsTrigger data-testid="tab-ceo-jobs" value="jobs" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">All Submissions</TabsTrigger>
      </TabsList>

      <TabsContent value="analytics" className="space-y-6">
        {analytics && (
          <>
            <div className="grid grid-cols-1 gap-px border border-border/60 bg-border/40 sm:grid-cols-2 lg:grid-cols-4">
              {cards.map(([label, val, Icon, color], i) => (
                <div key={label} className="bg-card p-6" data-testid={`analytics-card-${i}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
                    <Icon size={22} weight="duotone" className={color} />
                  </div>
                  <p className={`mt-3 font-display text-2xl font-black tracking-tight lg:text-3xl ${color}`}>{val}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="rounded-sm border border-border bg-card p-6 lg:col-span-2" data-testid="revenue-chart">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Monthly Revenue (RM)</p>
                <div className="mt-6 h-72">
                  {analytics.monthly.length ? (
                    <ResponsiveContainer width="100%" height="100%" minWidth={280} minHeight={280}>
                      <BarChart data={analytics.monthly}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#232a36" />
                        <XAxis dataKey="month" stroke="#64748b" fontSize={11} fontFamily="JetBrains Mono" />
                        <YAxis stroke="#64748b" fontSize={11} fontFamily="JetBrains Mono" />
                        <Tooltip contentStyle={{ background: "#14181f", border: "1px solid #232a36", borderRadius: 2, fontSize: 12 }} />
                        <Bar dataKey="revenue" fill="#F59E0B" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No revenue data yet</div>
                  )}
                </div>
              </div>

              <div className="rounded-sm border border-border bg-card p-6" data-testid="status-breakdown">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Job Status Mix</p>
                <div className="mt-6 space-y-4">
                  {Object.entries(analytics.status_counts).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between border-b border-border/50 pb-3">
                      <span className="text-sm font-semibold capitalize">{status.replace("_", " ")}</span>
                      <span className="font-mono text-sm font-bold text-primary">{count}</span>
                    </div>
                  ))}
                  {!Object.keys(analytics.status_counts).length && <p className="text-sm text-muted-foreground">No jobs yet</p>}
                </div>
              </div>
            </div>

            <div className="rounded-sm border border-border" data-testid="commission-table">
              <div className="border-b border-border bg-card px-6 py-4">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Commission Payouts by Staff</p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Staff</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Role</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Jobs</TableHead>
                    <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Commission (RM)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analytics.staff_commissions.map((s) => (
                    <TableRow key={`${s.role}-${s.name}`} className="border-border hover:bg-muted/50">
                      <TableCell className="font-semibold">{s.name}</TableCell>
                      <TableCell className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{s.role}</TableCell>
                      <TableCell className="font-mono">{s.jobs}</TableCell>
                      <TableCell className="text-right font-mono font-bold text-primary">{s.amount.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  {!analytics.staff_commissions.length && (
                    <TableRow><TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">No commission data yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </TabsContent>

      <TabsContent value="jobs">
        <SubmissionsTable submissions={subs} onRefresh={refresh} canEdit canUpdateStatus staff={staff} />
      </TabsContent>
    </Tabs>
  );
}
